import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { DatePicker, Button, message } from "antd";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ReactCountryFlag from "react-country-flag";
import moment from "moment";
import { toast } from "react-toastify";

import ReactTable from "../../reusableComponents/ReactTable/ReactTable";
import LoaderOverlay from "../Loader";
import { baseUrl } from "../../utils/api/apis";
import { country } from "../../utils/constants/country";
import { HtmlTooltip } from "../../reusableComponents/Tooltip";
import { ReactComponent as SearchNew } from "../../assets/icons/search-svgrepo-com (1).svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/CopyIcon.svg";
import { ReactComponent as BrandCopyIcon } from "../../assets/icons/BrandCopyIcon.svg";

const { RangePicker } = DatePicker;

// Debounce helper for search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const UserReportsList = () => {
  const { idToken } = useSelector((state) => state.auth);
  const { selectedBrand } = useSelector((state) => state.brand);

  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Search input state
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 400);

  // Date range state
  const [dateRange, setDateRange] = useState(null);

  // Table pagination state
  const [params, setParams] = useState({
    page: 1,
    page_size: 10,
  });

  const handleCopyToClipboard = () => {
    toast.success("Copied email successfully!", {
      position: "top-right",
      autoClose: 2000,
      theme: "dark",
    });
  };

  // Memoize columns definition
  const columns = useMemo(
    () => [
      {
        Header: "User / Email",
        accessor: "email",
        Cell: ({ value }) => {
          if (!value) return "-";
          
          // Generate a stable color based on email string
          let hash = 0;
          for (let i = 0; i < value.length; i++) {
            hash = value.charCodeAt(i) + ((hash << 5) - hash);
          }
          const hue = Math.abs(hash % 360);
          const avatarColor = `hsl(${hue}, 65%, 45%)`;

          return (
            <div className="column_one_wrapper" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                className="sno_wrapper"
                style={{
                  backgroundColor: avatarColor,
                  color: "#fff",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "14px"
                }}
              >
                {value.charAt(0)}
              </div>
              <div style={{ fontWeight: "500" }}>{value}</div>
              <CopyToClipboard text={value} onCopy={handleCopyToClipboard}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", marginLeft: "4px" }}>
                  {selectedBrand?.brand === "crypto_edge" ? (
                    <BrandCopyIcon style={{ width: "16px", height: "16px" }} />
                  ) : (
                    <CopyIcon style={{ width: "16px", height: "16px" }} />
                  )}
                </span>
              </CopyToClipboard>
            </div>
          );
        },
      },
      {
        Header: "Contact",
        accessor: "contact",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Country",
        accessor: "country",
        Cell: ({ value }) => {
          if (!value) return "-";
          const selectedCountry = country?.find(
            (c) => c?.country?.toLowerCase() === value?.toLowerCase() || c?.iso?.toLowerCase() === value?.toLowerCase()
          );

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {selectedCountry?.iso ? (
                <HtmlTooltip
                  title={selectedCountry.country}
                  placement="top"
                  arrow
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ReactCountryFlag
                      countryCode={selectedCountry.iso}
                      svg
                      style={{ width: "1.8em", height: "1.8em" }}
                      title={selectedCountry.country}
                    />
                  </div>
                </HtmlTooltip>
              ) : null}
              <span>{selectedCountry?.country || value}</span>
            </div>
          );
        },
      },
      {
        Header: "Date Joined",
        accessor: "date_joined",
        Cell: ({ value }) => {
          if (!value) return "-";
          return moment(value).format("YYYY-MM-DD hh:mm A");
        },
      },
    ],
    [selectedBrand]
  );

  // Fetch report data from API
  const fetchUserReports = async (currentParams = params) => {
    if (!idToken) return;

    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          page: currentParams.page,
          page_size: currentParams.page_size,
        },
      };

      if (debouncedSearchText) {
        config.params.search = debouncedSearchText;
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        config.params.start_date = dateRange[0].format("YYYY-MM-DD");
        config.params.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(
        `${baseUrl}auth/admin/user-report/`,
        config
      );

      let rows = [];
      let count = 0;

      if (Array.isArray(response.data)) {
        rows = response.data;
        count = response.data.length;
      } else if (Array.isArray(response.data.results)) {
        rows = response.data.results;
        count = response.data.count ?? response.data.results.length;
      } else if (Array.isArray(response.data.data)) {
        rows = response.data.data;
        count = response.data.total ?? response.data.data.length;
      }

      setReports(rows);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching User Reports:", error);
      message.error("Failed to load user reports.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to download CSV
  const downloadBlob = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Handle Export CSV
  const handleExportCsv = async () => {
    if (!idToken) return;

    setExportLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          export: "csv",
        },
        responseType: "blob",
      };

      if (debouncedSearchText) {
        config.params.search = debouncedSearchText;
      }

      let filename = "user-report.csv";
      if (dateRange && dateRange[0] && dateRange[1]) {
        const start_date = dateRange[0].format("YYYY-MM-DD");
        const end_date = dateRange[1].format("YYYY-MM-DD");
        config.params.start_date = start_date;
        config.params.end_date = end_date;
        filename = `user-report-${start_date}-to-${end_date}.csv`;
      }

      const response = await axios.get(
        `${baseUrl}auth/admin/user-report/`,
        config
      );

      downloadBlob(response.data, filename, "text/csv;charset=utf-8;");
      message.success("CSV exported successfully.");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      message.error("Failed to export CSV user reports.");
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch reports on filter/pagination/search updates
  useEffect(() => {
    // Reset to page 1 on search / dateRange change
    setParams((prev) => {
      if (prev.page !== 1) {
        return { ...prev, page: 1 };
      }
      return prev;
    });
    fetchUserReports({ ...params, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, dateRange, idToken]);

  // Fetch when page or page_size updates
  useEffect(() => {
    fetchUserReports(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.page_size]);

  return (
    <div className="table_scroll_wrapper">
      <div className="alltable">
        <div className="alltable_header" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
          
          {/* Search container */}
          <div className="search_container" style={{ display: "flex", alignItems: "center", position: "relative" }}>
            <input
              type="text"
              placeholder="Search user..."
              className="user-reports-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <div style={{ position: "absolute", right: "12px", pointerEvents: "none" }}>
              <SearchNew style={{ width: "20px", height: "20px" }} />
            </div>
          </div>

          {/* Date range picker and export CSV */}
          <div
            className="reports_filters"
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap"
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: "500", opacity: 0.85 }}>Date Range:</span>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              allowClear
              className="user-reports-datepicker"
            />
            <Button
              type="primary"
              onClick={handleExportCsv}
              loading={exportLoading}
              style={{
                height: "44px",
                borderRadius: "12px",
                padding: "0 24px",
                fontWeight: "600",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease"
              }}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {/* Table wrapper container */}
        <div className="table_wrapper" style={{ position: "relative" }}>
          {loading && <LoaderOverlay />}
          <ReactTable
            columns={columns}
            data={reports}
            serverSide={true}
            params={params}
            setParams={setParams}
            totalCount={totalCount}
          />
        </div>
      </div>
    </div>
  );
};

export default UserReportsList;
